<template>
  <div class="main-container">
    <transition name="fade" v-if="loading || !rhinoCode">
      <div class="page-loader">
        <ProjectLoader />
      </div>
    </transition>
    <section
      v-else-if="!loading && rhinoCode"
      id="project-container"
      :class="{
        'edit-mode': !isCreateMode,
        'create-mode': isCreateMode,
      }"
    >
      <!-- <button @click="redirectToLocker">GO TO LOCKER</button> -->
      <ProjectNavbar
        v-if="isCreateMode || dealerMode"
        :isCreateMode="isCreateMode"
        :isEditMode="isEditMode"
        :isSidebarOpen="isSidebarOpen"
        :stepsCompleted="stepsCompleted"
        :typeOfProject="project.typeOfProject"
        @openSidebar="openSidebar"
        @closeSidebar="closeSidebar"
        @openShareProjectModal="openShareProjectModal"
        @publishProject="handlePublishProject"
      />

      <main id="project-body" :class="{ 'create-mode': isCreateMode }">
        <DealerHeader
          v-if="!isCreateMode"
          @openDealerLoginModal="openDealerLoginModal"
        />
        <ProjectSidebar
          :isSidebarOpen="isSidebarOpen"
          :isSearchModalOpen="isSearchModalOpen"
          :errors="createProjectErrors"
          :sidebarHasErrors="sidebarHasErrors"
          @openSidebar="openSidebar()"
          @closeSidebar="closeSidebar()"
          @openSearchModal="openSearchModal()"
          @closeSearchModal="closeSearchModal()"
          @validateProjectSettings="validateProjectSettings()"
        />
        <!-- Open Sidebar button for mobile -->
        <button
          v-if="!isCreateMode"
          id="toggle-sidebar-mobile"
          @click="openSidebar()"
          :class="{ close: !isSidebarOpen }"
        >
          <img
            src="@/assets/icons/ui/filter-circle-outline.svg"
            alt="open sidebar"
          />
        </button>

        <PagesContainer
          :isSearchModalOpen="isSearchModalOpen"
          :activePageItem="activePageItem"
          @openSearchModal="openSearchModal"
          @closeSearchModal="closeSearchModal"
        >
          <template v-slot:hidding-modal>
            <transition name="fade">
              <div
                v-if="isEditModeActive"
                class="hidding-modal"
                @click="() => changeEditMode(false)"
              ></div>
            </transition>
          </template>
        </PagesContainer>
        <PaginationSidebar
          v-if="isCreateMode || (isPlaybook && project.paginationActive)"
        />

        <transition name="fade">
          <NewLockerProductsPreviewModal
            v-if="showLockerPreviewModalOpen"
            :isModalOpen="showLockerPreviewModalOpen"
            :lockerProductsIds="previewLockerProductsIds"
            @onCloseModal="handleRedirectToProject"
            @redirectToLocker="redirectToLocker"
          />
        </transition>
      </main>
      <transition name="fade">
        <DealerLoginModal
          v-if="showDealerLoginModal"
          @close="closeDealerLoginModal"
      /></transition>
      <transition name="fade">
        <ShareProjectModal
          v-if="showShareProjectModal"
          :isModalOpen="showShareProjectModal"
          @close="closeShareProjectModal"
      /></transition>
      <transition name="fade">
        <PreventProjectDraftModal
          v-show="showPreventProjectDraftModal"
          :isModalOpen="showPreventProjectDraftModal"
          @close="closePreventProjectDraftModal"
      /></transition>
      <!-- <transition name="fade">
        <ActiveProductModal
          v-if="showActiveProductModal"
          @close="closeActiveProductModal"
      /></transition> -->
      <transition name="fade">
        <UploadLogosModal @saveLogo="handleSaveLogo"
      /></transition>
      <transition name="fade">
        <ExportToPDFModal v-if="project.exportingToPDF" />
      </transition>
      <transition name="fade">
        <SnapsheetEditNewUniformProductModal
          v-if="showSnapsheetUniformProductModal"
        />
      </transition>
      <transition name="fade">
        <ModalAlert
          v-if="showAlertModal"
          :title="'Error'"
          :text="'You must complete all the fields '"
          :isError="true"
          @closeModal="showAlertModal = false"
        />
      </transition>
      <transition name="fade">
        <DesignsModal v-if="showDesignsModal" />
      </transition>
    </section>
    <!-- Threekit invisble decoration player -->
    <section>
      <div id="decorations-player-container">
        <div id="decorations-player"></div>
      </div>
      <div>
        <img
          :src="deco"
          v-for="(deco, i) in decorations"
          :key="'decoration-' + i"
        />
      </div>
    </section>
    <!-- Project overlay for complete/archived/closed projects -->
    <ProjectLockOverlay v-if="projectIsLocked" />
  </div>
</template>

<script>
import DealerHeader from "@/components/Projects/Project/DealerHeader";
import DealerLoginModal from "@/components/Projects/Project/DealerLoginModal";
import ExportToPDFModal from "@/components/Projects/Project/ExportToPDFModal";
import ProjectLoader from "@/components/Utils/ProjectLoader";
import ModalAlert from "@/components/Utils/ModalAlert.vue";
import PagesContainer from "@/components/Projects/Project/PagesContainer";
import PaginationSidebar from "@/components/Projects/Project/Sidebar/PaginationSidebar";
import ProjectLockOverlay from "@/components/Projects/Project/ProjectLockOverlay";
import ProjectNavbar from "@/components/Projects/Project/ProjectNavbar";
import ProjectSidebar from "@/components/Projects/Project/Sidebar/ProjectSidebar";
import ShareProjectModal from "@/components/Projects/Project/ShareProjectModal.vue";
import PreventProjectDraftModal from "@/components/Projects/Project/PreventProjectDraftModal.vue";
import SnapsheetEditNewUniformProductModal from "@/components/Projects/Snapsheets/SnapsheetEditNewUniformProductModal.vue";
import UploadLogosModal from "@/components/Utils/Logos/UploadLogosModal";
import DesignsModal from "@/components/Projects/Playbooks/Designs/DesignsModal";
import { mapActions, mapGetters, mapState, mapMutations } from "vuex";
import CONSTANTS from "@/constants";
export default {
  name: "Project",
  components: {
    DealerHeader,
    DealerLoginModal,
    ExportToPDFModal,
    ProjectLoader,
    ModalAlert,
    PaginationSidebar,
    PagesContainer,
    ProjectNavbar,
    ProjectLockOverlay,
    ProjectSidebar,
    SnapsheetEditNewUniformProductModal,
    ShareProjectModal,
    PreventProjectDraftModal,
    UploadLogosModal,
    DesignsModal,
  },

  data() {
    return {
      quantityOfLockerProductsPreviewToDisplay: 1, // This could be 2 or 3 (For Instant Playbooks)

      isSidebarOpen: false,
      isSearchModalOpen: false,
      showDealerLoginModal: false,
      showShareProjectModal: false,
      showPreventProjectDraftModal: false,
      showUploadLogosModal: false,
      showAlertModal: false,

      //Here we will have the data of the space being edited or beign filled with a new product
      //Snapsheet example: We have to store a new product that is being created in manual mode
      activePageItem: {
        pageId: null,
        order: null,
      },

      //Project Form Validations
      createProjectErrors: {
        title: "",
        rep: "",
        color_1: "",
        color_2: "",
        projectIsEmpty: false,
      },

      //Threekit test data
      openThreeKit: false,
    };
  },

  computed: {
    ...mapState(["project"]),
    ...mapState("project", [
      "showLockerPreviewModalOpen",
      "designsModalProduct",
    ]),
    ...mapState(["loading"]),
    ...mapState("auth", ["dealerMode"]),
    ...mapState("uploadLogos", ["logoModalAction"]),
    ...mapState("snapsheet", ["isEditModeActive"]),
    ...mapState(["lockerModule"]),
    ...mapGetters("project", [
      "isSnapsheet",
      "isPlaybook",
      "isCreateMode",
      "isEditMode",
      "projectTitle",
      "projectRep",
      "projectStatus",
      "projectColors",
      "projectIsLocked",
      "projectProducts",
      "projectTeamName",
      "showSnapsheetUniformProductModal",
    ]),
    ...mapState("threekit", ["decorations"]),
    ...mapGetters("snapsheet", ["getLastSnapsheetItem"]),
    ...mapGetters("dealer", ["rhinoCode"]),

    checkTitleIsValid() {
      return this.projectTitle?.length > 1;
    },

    checkColorsAreValid() {
      //  1st color is Required.
      //  2nd color is required for Instant Playbooks
      if (this.isPlaybook) {
        return this.projectColors[0]?.color && this.projectColors[1]?.color;
      } else if (this.isSnapsheet) {
        return this.projectColors[0]?.color;
      }
      return false;
    },

    checkRepIsValid() {
      return !!this.projectRep?.id;
    },

    previewLockerProductsIds() {
      const products = this.lockerModule.locker.products.slice(
        -Math.abs(this.quantityOfLockerProductsPreviewToDisplay)
      ); //Display the last 1, 2 or 3 elements of the locker

      return products.map((pr) => pr.lockerProductId);
    },

    sidebarHasErrors() {
      return Object.keys(this.createProjectErrors).some(
        (error) => this.createProjectErrors[error]
      );
    },

    showActiveProduct() {
      return (
        this.project.activeProduct != null && this.project.activeProduct.id
      );
    },

    stepsCompleted() {
      let stepsCompleted = 0;

      if (this.checkTitleIsValid) stepsCompleted++;
      if (this.checkColorsAreValid) stepsCompleted++;
      if (this.checkRepIsValid) stepsCompleted++;
      return stepsCompleted;
    },

    showDesignsModal() {
      return this.designsModalProduct;
    },
  },

  methods: {
    ...mapActions("project", [
      "editProjectLogo",
      "initProject",
      "publishProject",
      "setActiveProduct",
      "setEditMode",
      "uploadProjectLogo",
      "redirectToProjectPage",
      "resetProject",
      "showSaveIndicator",
      "updateDecorationsForProjectProductsLayouts",
      "endLoading",
    ]),
    ...mapActions("snapsheet", ["changeEditMode", "calculatePages"]),
    ...mapActions("dealer", ["getDealer"]),
    ...mapActions(["triggerStartLoading", "triggerEndLoading"]),
    ...mapMutations("project", ["TOGGLE_LOCKER_PREVIEW_MODAL"]),

    async handleInitProject() {
      if (this.isCreateMode) {
        //Only call to initProject if we have the RhinoCode (loaded from dashboard middleware)
        this.rhinoCode && (await this.initProject());
      } else {
        //Call initProject, there we will get the rhinoCode
        await this.initProject();
      }
    },

    async handlePublishProject() {
      try {
        //Validate the project data
        this.validateProjectSettings();

        if (!this.sidebarHasErrors) {
          await this.publishProject();
          this.closeSidebar();
          this.openShareProjectModal();
        } else {
          this.showAlertModal = true;
        }
      } catch (error) {
        console.log(error);
      }
    },

    handleRedirectToProject() {
      this.redirectToProjectPage();
      this.TOGGLE_LOCKER_PREVIEW_MODAL();
    },

    async handleSaveLogo({ logo, typesOfLogo }) {
      try {
        this.showSaveIndicator();
        if (this.logoModalAction === "upload") {
          await this.uploadProjectLogo({ logo, typesOfLogo });
        } else if (this.logoModalAction === "edit") {
          this.editProjectLogo(logo);
        }
        //Update PLP Decorations
        await this.updateDecorationsForProjectProductsLayouts();
      } catch (error) {
        console.log(error);
      } finally {
        this.endLoading();
      }
    },

    validateProjectSettings() {
      //reset errors
      this.createProjectErrors = {
        title: "",
        teamName: "",
        rep: "",
        color_1: "",
        color_2: "",
        projectIsEmpty: false,
      };

      //Check title
      if (!this.checkTitleIsValid) {
        this.createProjectErrors.title = "Please enter a title";
      }

      //Check Colors.
      //  1st color is Required.
      //  2nd color is required for Instant Playbooks
      if (this.isPlaybook) {
        if (!this.checkColorsAreValid) {
          this.createProjectErrors.color_1 = "Please enter at least 2 colors";
        }
        if (!this.projectTeamName) {
          this.createProjectErrors.teamName = "Please enter a Team name";
        }
      } else if (this.isSnapsheet) {
        if (!this.checkColorsAreValid) {
          this.createProjectErrors.color_1 = "Please enter a color";
        }
      }

      //Check Rep
      if (!this.checkRepIsValid) {
        this.createProjectErrors.rep = "Please select a Sales rep";
      }
    },

    redirectToLocker() {
      const typeOfProject = this.project.typeOfProject + "s";
      const { dealer_code, project_id } = this.$route.params;

      this.$router.push(
        `/${typeOfProject}/${dealer_code}/${project_id}/locker`
      );

      this.TOGGLE_LOCKER_PREVIEW_MODAL();
    },

    openDealerLoginModal() {
      this.showDealerLoginModal = true;
    },

    closeDealerLoginModal() {
      this.showDealerLoginModal = false;
    },

    openSidebar() {
      this.isSidebarOpen = true;
      //Set edit mode when the dealer opens the sidebar
      if (this.dealerMode && !this.isCreateMode) this.setEditMode();
    },

    closeSidebar() {
      this.isSidebarOpen = false;
    },

    openSearchModal(pr) {
      //The emit was triggered by a swap o add new product event
      this.activePageItem = pr;
      this.isSearchModalOpen = true;
    },

    closeSearchModal() {
      this.isSearchModalOpen = false;
    },

    openShareProjectModal() {
      this.showShareProjectModal = true;
    },

    closeShareProjectModal() {
      this.showShareProjectModal = false;
    },
    openPreventProjectDraftModal() {
      this.showPreventProjectDraftModal = true;
    },

    closePreventProjectDraftModal() {
      this.showPreventProjectDraftModal = false;
    },

    closeActiveProduct() {
      this.setActiveProduct({ product: null });
    },
  },

  async mounted() {
    if (this.isCreateMode) {
      //Show to the user a
      // window.top.onbeforeunload = (event) =>{
      //   event.preventDefault();
      //   event.returnValue = "";
      //   this.openPreventProjectDraftModal();
      // };
      window.onbeforeunload = (event) => {
        event.preventDefault();
        event.returnValue = "";
        this.openPreventProjectDraftModal();
      };
    }
    if (this.isEditMode) {
      this.openShareProjectModal();
    }
    await this.handleInitProject();
  },

  destroyed() {
    //Reset project if the user leaves the page (except for the locker route)
    if (
      this.$route.name !== "Snapsheet Locker" &&
      this.$route.name !== "Playbook Locker" &&
      this.$route.name !== "Customize Your Product" &&
      this.projectStatus !== CONSTANTS.PROJECT_STATUS.PUBLISHED
    ) {
      this.resetProject();
    }
  },

  watch: {
    async rhinoCode() {
      //Only call to initProject if we have the RhinoCode (loaded from dashboard middleware)
      this.isCreateMode && (await this.handleInitProject());
    },

    dealerMode() {
      this.calculatePages();
    },
  },

  props: ["typeOfProject"],
};
</script>

<style lang="scss" scoped>
.main-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100vh;
  position: relative;
  background: $grey-lightest;
  transition: all 0.2s;

  * {
    color: black;
  }

  #project-container {
    &.edit-mode {
      padding-left: 6rem;
    }
    #project-body {
      display: flex;
      flex-wrap: wrap;
      padding: 1rem;

      &.create-mode {
        padding-left: 6rem;
        position: relative;
        flex-wrap: nowrap;
        padding: 0;
        padding-left: 5%;
        max-height: 100vh;
      }

      @media screen and (max-width: 768px) {
        padding-left: 1%;
        &.create-mode {
          padding-left: 1%;
        }
      }
    }

    #toggle-sidebar-mobile {
      display: none;

      @media screen and (max-width: 576px) {
        display: flex;
        width: 3rem;
        height: 3rem;
        position: absolute;
        z-index: 10;
        left: 1rem;
        top: 1rem;
        background: #ececec;
        border: 1px solid black;

        img {
          width: 100%;
          height: 100%;
        }
      }
    }

    @media screen and (max-width: 576px) {
      &.edit-mode {
        padding-left: 0;
      }
    }
  }

  .page-loader {
    width: 100vw;
    height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.15s;
  }
  .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
  }

  .hidding-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #00000069;
    z-index: 1;
  }

  .dealer-menu-button {
    background-color: #fffcd8;
    color: #000;
    text-transform: uppercase;
    width: 180px;
    padding: 7px;
    cursor: pointer;
    border: none;
    -webkit-box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.35);
    -moz-box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.35);
    box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.35);
  }
}

#decorations-player {
  visibility: hidden;
  position: absolute;
  top: -100vh;
  left: -100vw;
  width: 100%;
  height: 100%;
}
</style>
